/**
 * titleFieldsView — turn a title-page ``title_fields`` dict (label→value) into a
 * plain render model for the in-panel previews (DRAFT tab, Action History).
 *
 * A title-page module has NO prose body — its markdown ``draft_content`` is
 * empty and its content lives entirely in ``title_fields`` (e.g. STUDY_TITLE,
 * PROTOCOL_NUMBER, SPONSOR_NAME, plus statement fields). The Word document
 * renders these via ``wordWriter.ts::updateTitlePageFields``; this module is the
 * read-only preview equivalent so the panel matches the cover.
 *
 * The label/cover/normalize helpers below are faithful ports of the private,
 * Office-coupled helpers in ``wordWriter.ts`` — ``_resolveFieldLabel`` (5118),
 * ``_normFieldKey`` (5151), ``_isCoverTitleKey`` (5158). Kept here as small pure
 * functions (no Office dependency) rather than imported from that large module.
 * KEEP IN SYNC with wordWriter.ts if the field grammar changes.
 *
 * Note: the Word writer further splits non-cover fields into "statement" fields
 * (centered paragraphs) vs table rows, using the doc-type YAML's
 * ``statement_fields``. That config is not available to the preview, so every
 * non-cover field renders as a labeled row here. Faithful enough for a
 * read-only preview.
 */

/** Normalize a title-field label for matching: trim, drop trailing colons,
 *  lowercase. Mirror of wordWriter.ts::_normFieldKey / word_writer.py. */
export function normFieldKey(key: string): string {
  return key.trim().replace(/:+$/, "").trim().toLowerCase();
}

/** True when the field is the one promoted to the large centered cover heading
 *  (and therefore excluded from the metadata rows). Mirror of
 *  wordWriter.ts::_isCoverTitleKey. */
export function isCoverTitleKey(key: string): boolean {
  const norm = normFieldKey(key);
  return norm === "study title" || norm === "study_title";
}

/** Resolve a field key to its human-readable label (with trailing colon).
 *  Mirror of wordWriter.ts::_resolveFieldLabel. */
export function resolveFieldLabel(fieldName: string): string {
  // Strategy 1: Direct — agent sends exact table labels like "Device Name:".
  // Detect by: contains a space, or already ends with ":".
  if (fieldName.includes(" ") || fieldName.endsWith(":")) {
    return fieldName.endsWith(":") ? fieldName : `${fieldName}:`;
  }

  // Strategy 2: CSR legacy mappings (backward compatibility).
  const csrMappings: Record<string, string> = {
    STUDY_TITLE: "Study Title:",
    PROTOCOL_NUMBER: "Protocol Number:",
    COMPOUND_NAME: "Compound:",
    REGULATORY_PHASE: "Phase:",
    INDICATION: "Indication:",
    SPONSOR_NAME: "Sponsor:",
    DATE_REPORT: "Report Date:",
    DATE_FPFV: "Study Initiation Date:",
    COMPARATOR: "Compound Code:",
  };
  if (csrMappings[fieldName]) return csrMappings[fieldName];

  // Strategy 3: Convert UPPER_SNAKE to Title Case: DEVICE_NAME → Device Name:
  const titleCase = fieldName
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
  return `${titleCase}:`;
}

export interface TitleFieldRow {
  /** Original field key (stable React key). */
  key: string;
  /** Human-readable label including trailing colon. */
  label: string;
  /** Field value. May be raw markdown (including a markdown table). */
  value: string;
}

export interface TitleFieldsView {
  /** The promoted study-title heading, or null when absent. */
  coverTitle: string | null;
  /** Every other non-empty field, in insertion order. */
  rows: TitleFieldRow[];
}

/**
 * Partition a ``title_fields`` dict into a cover title + labeled rows. Fields
 * with an empty/whitespace value are dropped (mirrors the Word writer, which
 * filters ``([, v]) => v``). The cover-title field is promoted to
 * ``coverTitle`` and excluded from ``rows``.
 */
export function partitionTitleFields(
  fields: Record<string, string> | null | undefined,
): TitleFieldsView {
  const view: TitleFieldsView = { coverTitle: null, rows: [] };
  if (!fields) return view;

  for (const [key, rawValue] of Object.entries(fields)) {
    const value = (rawValue ?? "").trim();
    if (!value) continue;
    if (isCoverTitleKey(key)) {
      // Last cover-title write wins, matching the writer's find semantics.
      view.coverTitle = value;
      continue;
    }
    view.rows.push({ key, label: resolveFieldLabel(key), value });
  }
  return view;
}

/** True when a title_fields dict has any renderable content. */
export function hasTitleFields(fields: Record<string, string> | null | undefined): boolean {
  const view = partitionTitleFields(fields);
  return view.coverTitle !== null || view.rows.length > 0;
}
